import { useState, useEffect, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import clsx from 'clsx';
import {
  useEscapeData,
  useFilteredEscapes,
  useBuckets,
  useTrendDelta,
  usePriorityBreakdown,
  useStatusRatio,
  useHeatmapSeries,
  useMomentum,
  useMttr,
  useMttrByMonth,
  PRIORITY_ORDER,
  OPEN_STATUSES,
  dateOffset,
} from '@Lib/escapeTracker/useEscapeData';
import { Timeframe, ViewMode, ChartType } from '@Lib/escapeTracker/types';

const COLORS = [
  '#2563EB',
  '#DC2626',
  '#16A34A',
  '#D97706',
  '#7C3AED',
  '#0891B2',
  '#DB2777',
  '#65A30D',
  '#EA580C',
  '#6D28D9',
  '#0D9488',
  '#B91C1C',
  '#1D4ED8',
  '#B45309',
  '#047857',
  '#9333EA',
  '#0E7490',
  '#BE185D',
];

const PRIORITY_COLORS: Record<string, string> = {
  Highest: '#DC2626',
  High: '#EA580C',
  Medium: '#D97706',
  Low: '#2563EB',
  'No Priority Set': '#64748B',
  Lowest: '#16A34A',
};

function getColor(index: number): string {
  return COLORS[index % COLORS.length];
}

function buildJiraUrl(
  proj: string,
  dateStart: string,
  dateEnd: string
): string {
  return (
    `https://trajectorservices.atlassian.net/issues/?jql=` +
    `issuetype%20%3D%20Bug%20AND%20%22Discovery%20Environment%22%20%3D%20%22Production%22` +
    `%20AND%20project%20%3D%20${proj}` +
    `%20AND%20created%20>%3D%20%22${dateStart}%22` +
    `%20AND%20created%20<%3D%20%22${dateEnd}%22` +
    `%20ORDER%20BY%20created%20DESC`
  );
}

function getWeekEnd(weekStart: string): string {
  return dateOffset(weekStart, 6);
}

function getMonthEnd(monthKey: string): string {
  const [y, m] = monthKey.split('-');
  return new Date(+y, +m, 0).toISOString().slice(0, 10);
}

function getMonthStart(monthKey: string): string {
  return monthKey + '-01';
}

type SortCol = 'allTime' | 'last30' | 'pctOpen';
type SortDir = 'asc' | 'desc';

function getPctOpenColor(pct: number): string {
  if (pct >= 60) return 'bg-red-100 text-red-700';
  if (pct >= 30) return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}

function getArrowColor(arrow: '↑' | '→' | '↓'): string {
  if (arrow === '↑') return 'text-red-500';
  if (arrow === '↓') return 'text-emerald-600';
  return 'text-slate-400';
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0)
    return <span className="ml-1 text-xs text-slate-400">= vs prev wk</span>;
  const up = delta > 0;
  return (
    <span
      className={clsx(
        'ml-1 text-xs font-semibold',
        up ? 'text-red-500' : 'text-emerald-600'
      )}
    >
      {up ? `+${delta} ↑` : `${delta} ↓`} vs prev wk
    </span>
  );
}

function SortIcon({
  col,
  sortCol,
  sortDir,
}: {
  col: SortCol;
  sortCol: SortCol;
  sortDir: SortDir;
}) {
  if (sortCol !== col) return <span className="ml-1 text-slate-300">↕</span>;
  return (
    <span className="ml-1 text-blue-500">{sortDir === 'desc' ? '↓' : '↑'}</span>
  );
}

const EscapeTrackerPage = () => {
  const { data, loading, error } = useEscapeData();

  const [view, setView] = useState<ViewMode>('weekly');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [timeframe, setTimeframe] = useState<Timeframe>('all');
  const [activeProjects, setActiveProjects] = useState<Set<string> | null>(
    null
  );
  const [soloProject, setSoloProject] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<SortCol>('allTime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedPieProject, setSelectedPieProject] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (data && activeProjects === null) {
      setActiveProjects(new Set(data.projects));
    }
  }, [data, activeProjects]);

  const effectiveActive = useMemo(
    () => activeProjects ?? (data ? new Set(data.projects) : new Set<string>()),
    [activeProjects, data]
  );

  const filteredItems = useFilteredEscapes(data, timeframe, effectiveActive);
  const {
    keys: bucketKeys,
    b: buckets,
    labels: bucketLabels,
  } = useBuckets(filteredItems, view);

  const trendDelta = useTrendDelta(filteredItems, data?.summary.run_date ?? '');
  const priorityBreakdown = usePriorityBreakdown(filteredItems);
  const statusRatio = useStatusRatio(filteredItems);
  const heatmapData = useHeatmapSeries(filteredItems);

  const momentum = useMomentum(data, effectiveActive);
  const mttr = useMttr(filteredItems);
  const mttrByMonth = useMttrByMonth(filteredItems);

  const kpiFilteredCount = filteredItems.length;

  const kpiProjectsAffected = useMemo(() => {
    return new Set(filteredItems.map((e) => e.project_key)).size;
  }, [filteredItems]);

  const kpiPeak = useMemo(() => {
    if (bucketKeys.length === 0) return { label: '—', count: 0 };
    let maxKey = bucketKeys[0];
    let maxCount = 0;
    bucketKeys.forEach((k) => {
      const total = Object.values(buckets[k] || {}).reduce((s, v) => s + v, 0);
      if (total > maxCount) {
        maxCount = total;
        maxKey = k;
      }
    });
    const idx = bucketKeys.indexOf(maxKey);
    return { label: bucketLabels[idx] ?? maxKey, count: maxCount };
  }, [bucketKeys, buckets, bucketLabels]);

  const kpiAvgPerWeek = useMemo(() => {
    if (bucketKeys.length === 0) return '0.0';
    if (view === 'weekly')
      return (filteredItems.length / Math.max(bucketKeys.length, 1)).toFixed(1);
    return (
      filteredItems.length / Math.max(bucketKeys.length * 4.33, 1)
    ).toFixed(1);
  }, [filteredItems, bucketKeys, view]);

  const topOffendersData = useMemo(() => {
    if (!data)
      return [] as {
        project: string;
        allTime: number;
        last30: number;
        pctOpen: number;
        topPriority: string;
      }[];
    const runStr = data.summary.run_date;
    const l30Str = dateOffset(runStr, -30);

    type Stats = {
      last30: number;
      openCount: number;
      prioritySet: Set<string>;
    };
    const stats: Record<string, Stats> = {};
    data.raw.forEach((e) => {
      if (!stats[e.project_key])
        stats[e.project_key] = {
          last30: 0,
          openCount: 0,
          prioritySet: new Set(),
        };
      const s = stats[e.project_key];
      if (e.created_date >= l30Str && e.created_date <= runStr) s.last30++;
      if (OPEN_STATUSES.has(e.status)) s.openCount++;
      s.prioritySet.add(e.priority);
    });

    const rows = data.projects.map((proj) => {
      const s = stats[proj] ?? {
        last30: 0,
        openCount: 0,
        prioritySet: new Set<string>(),
      };
      const allTime = data.proj_totals[proj] ?? 0;
      const pctOpen =
        allTime > 0 ? Math.round((s.openCount / allTime) * 100) : 0;
      const topPriority =
        PRIORITY_ORDER.find((p) => s.prioritySet.has(p)) ?? '—';
      return { project: proj, allTime, last30: s.last30, pctOpen, topPriority };
    });

    const dir = sortDir === 'desc' ? -1 : 1;
    return rows.sort((a, b) => {
      if (sortCol === 'last30') return dir * (a.last30 - b.last30);
      if (sortCol === 'pctOpen') return dir * (a.pctOpen - b.pctOpen);
      return dir * (a.allTime - b.allTime);
    });
  }, [data, sortCol, sortDir]);

  const handleSortToggle = (col: SortCol) => {
    if (sortCol === col) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const chartSeries = useMemo(() => {
    if (!data)
      return [] as {
        name: string;
        type?: string;
        data: number[];
        color: string;
      }[];
    const projectSeries: {
      name: string;
      type?: string;
      data: number[];
      color: string;
    }[] = data.projects
      .filter((p) => effectiveActive.has(p))
      .map((proj) => ({
        name: proj,
        type: chartType === 'bar' ? 'bar' : 'line',
        data: bucketKeys.map((k) => buckets[k]?.[proj] ?? 0),
        color: getColor(data.projects.indexOf(proj)),
      }))
      .filter((s) => s.data.some((v) => v > 0) || effectiveActive.has(s.name));

    if (chartType === 'bar' && bucketKeys.length >= 2) {
      const avgData = bucketKeys.map((_, i) => {
        const window = bucketKeys.slice(Math.max(0, i - 3), i + 1);
        const total = window.reduce((sum, k) => {
          return (
            sum + Object.values(buckets[k] || {}).reduce((s, v) => s + v, 0)
          );
        }, 0);
        return parseFloat((total / window.length).toFixed(1));
      });
      projectSeries.push({
        name: '4-wk avg',
        type: 'line',
        data: avgData,
        color: '#94A3B8',
      });
    }
    return projectSeries;
  }, [data, bucketKeys, buckets, effectiveActive, chartType]);

  const chartOptions = useMemo((): ApexOptions => {
    const hasAvg = chartType === 'bar' && bucketKeys.length >= 2;
    const nProj = chartSeries.filter((s) => s.name !== '4-wk avg').length;
    return {
      chart: {
        type: chartType === 'bar' ? 'bar' : 'line',
        stacked: chartType === 'bar',
        height: 360,
        toolbar: { show: false },
        background: 'transparent',
        events: {
          dataPointSelection: (_event, _ctx, config) => {
            if (!data) return;
            const bucketIdx = config.dataPointIndex as number;
            const seriesIdx = config.seriesIndex as number;
            const key = bucketKeys[bucketIdx];
            if (!key) return;
            const proj = chartSeries[seriesIdx]?.name ?? '';
            if (proj === '4-wk avg') return;
            let dateStart: string;
            let dateEnd: string;
            if (view === 'weekly') {
              dateStart = key;
              dateEnd = getWeekEnd(key);
            } else {
              dateStart = getMonthStart(key);
              dateEnd = getMonthEnd(key);
            }
            window.open(buildJiraUrl(proj, dateStart, dateEnd), '_blank');
          },
        },
      },
      colors: chartSeries.map((s) => s.color),
      plotOptions: {
        bar: { columnWidth: '60%', borderRadius: 3 },
      },
      dataLabels: { enabled: false },
      stroke: hasAvg
        ? { width: [...Array(nProj).fill(0), 2.5], curve: 'smooth' }
        : chartType === 'line'
          ? { width: 2, curve: 'smooth' }
          : { width: 0 },
      markers: hasAvg
        ? { size: [...Array(nProj).fill(0), 4] }
        : chartType === 'line'
          ? { size: 3 }
          : { size: 0 },
      xaxis: {
        categories: bucketLabels,
        labels: {
          style: { fontSize: '11px', colors: '#94A3B8' },
          rotate: -35,
          maxHeight: 80,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { fontSize: '11px', colors: '#94A3B8' } } },
      grid: { borderColor: '#F1F5F9', strokeDashArray: 4 },
      legend: { show: false },
      tooltip: {
        shared: false,
        intersect: true,
        theme: 'light',
        y: {
          formatter: (val: number, opts: { seriesIndex: number }) => {
            const name = chartSeries[opts?.seriesIndex]?.name;
            if (name === '4-wk avg') return `${val} avg/wk`;
            return `${val} escape${val !== 1 ? 's' : ''}`;
          },
        },
      },
      fill: { type: 'solid', opacity: chartType === 'bar' ? 0.85 : 0.8 },
    };
  }, [chartType, chartSeries, bucketLabels, bucketKeys, data, view]);

  const pieData = useMemo(() => {
    const totals: Record<string, number> = {};
    filteredItems.forEach((e) => {
      totals[e.project_key] = (totals[e.project_key] ?? 0) + 1;
    });
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [filteredItems]);

  const pieTotal = useMemo(
    () => pieData.reduce((s, [, v]) => s + v, 0),
    [pieData]
  );

  const pieOptions = useMemo((): ApexOptions => {
    if (!data) return {};
    const pieColors = pieData.map(([proj]) =>
      getColor(data.projects.indexOf(proj))
    );
    return {
      chart: {
        type: 'donut',
        height: 280,
        toolbar: { show: false },
        background: 'transparent',
        events: {
          dataPointSelection: (
            _e: unknown,
            _ctx: unknown,
            config: { dataPointIndex: number }
          ) => {
            const proj = pieData[config.dataPointIndex]?.[0] ?? null;
            setSelectedPieProject((prev) => (prev === proj ? null : proj));
          },
        },
      },
      colors: pieColors,
      labels: pieData.map(([p]) => p),
      stroke: { width: 2, colors: ['#fff'] },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '13px',
                fontWeight: 600,
                color: '#1E293B',
                formatter: () => String(pieTotal),
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} escape${val !== 1 ? 's' : ''}`,
        },
      },
    };
  }, [data, pieData, pieTotal]);

  const priorityChartOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'bar',
        toolbar: { show: false },
        background: 'transparent',
      },
      colors: priorityBreakdown.map(
        (p) => PRIORITY_COLORS[p.priority] ?? '#64748B'
      ),
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          borderRadius: 4,
          barHeight: '60%',
        },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: '11px', colors: ['#fff'], fontWeight: 600 },
        formatter: (val: number) => String(val),
      },
      xaxis: {
        categories: priorityBreakdown.map((p) => p.priority),
        labels: { style: { fontSize: '11px', colors: '#94A3B8' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { fontSize: '11px', colors: '#475569' } } },
      grid: {
        borderColor: '#F1F5F9',
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      legend: { show: false },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} escape${val !== 1 ? 's' : ''}`,
        },
      },
    }),
    [priorityBreakdown]
  );

  const statusDonutOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'donut',
        toolbar: { show: false },
        background: 'transparent',
      },
      colors: ['#3B82F6', '#10B981'],
      labels: ['Open', 'Closed'],
      stroke: { width: 2, colors: ['#fff'] },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Open',
                fontSize: '13px',
                fontWeight: 600,
                color: '#1E293B',
                formatter: () => String(statusRatio.open),
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '12px',
        labels: { colors: '#64748B' },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} escape${val !== 1 ? 's' : ''}`,
        },
      },
    }),
    [statusRatio]
  );

  const mttrByMonthOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'line',
        toolbar: { show: false },
        background: 'transparent',
        animations: { enabled: false },
      },
      colors: mttrByMonth.prioritiesWithData.map(
        (p) => PRIORITY_COLORS[p] ?? '#64748B'
      ),
      stroke: { width: 2.5, curve: 'smooth' },
      markers: { size: 5 },
      xaxis: {
        categories: mttrByMonth.monthLabels,
        labels: { style: { fontSize: '11px', colors: '#94A3B8' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { fontSize: '11px', colors: '#94A3B8' },
          formatter: (val: number) => `${Math.round(val)}d`,
        },
        title: {
          text: 'Avg days to Done',
          style: { fontSize: '11px', color: '#94A3B8' },
        },
      },
      grid: { borderColor: '#F1F5F9', strokeDashArray: 4 },
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        fixed: {
          enabled: true,
          position: 'topRight',
          offsetX: 0,
          offsetY: 0,
        },
        y: {
          formatter: (val: number | null) =>
            val == null ? '— (n<3)' : `${val}d avg`,
        },
      },
      noData: { text: 'Insufficient resolved data' },
    }),
    [mttrByMonth.prioritiesWithData, mttrByMonth.monthLabels]
  );

  const heatmapOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'heatmap',
        toolbar: { show: false },
        background: 'transparent',
      },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.6,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: '#F8FAFC', name: 'None' },
              { from: 1, to: 3, color: '#FEF9C3', name: 'Low' },
              { from: 4, to: 7, color: '#FCD34D', name: 'Medium' },
              { from: 8, to: 999, color: '#DC2626', name: 'High' },
            ],
          },
        },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: '10px', colors: ['#374151'], fontWeight: 600 },
        formatter: (val: number) => (val === 0 ? '' : String(val)),
      },
      xaxis: {
        labels: {
          style: { fontSize: '10px', colors: '#94A3B8' },
          rotate: -35,
          maxHeight: 70,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { fontSize: '10px', colors: '#475569' } } },
      grid: { show: false },
      stroke: { width: 1, colors: ['#fff'] },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} escape${val !== 1 ? 's' : ''}`,
        },
      },
    }),
    []
  );

  const handleLegendClick = (proj: string, isMeta: boolean) => {
    if (!data) return;
    setSelectedPieProject(null);
    if (isMeta) {
      if (soloProject === proj && effectiveActive.size === 1) {
        setActiveProjects(new Set(data.projects));
        setSoloProject(null);
      } else {
        setActiveProjects(new Set([proj]));
        setSoloProject(proj);
      }
    } else {
      setSoloProject(null);
      const next = new Set(effectiveActive);
      if (next.has(proj)) {
        if (next.size > 1) next.delete(proj);
      } else next.add(proj);
      setActiveProjects(next);
    }
  };

  const handleLegendAll = () => {
    if (data) setActiveProjects(new Set(data.projects));
    setSoloProject(null);
    setSelectedPieProject(null);
  };

  const handleTopFive = () => {
    const top5 = new Set(pieData.slice(0, 5).map(([proj]) => proj));
    setActiveProjects(top5);
    setSoloProject(null);
    setSelectedPieProject(null);
  };

  const isTopFiveActive = useMemo(() => {
    if (!data || effectiveActive.size !== 5) return false;
    const top5 = pieData.slice(0, 5).map(([proj]) => proj);
    return top5.every((p) => effectiveActive.has(p));
  }, [data, effectiveActive, pieData]);

  const handleReset = () => {
    setView('weekly');
    setChartType('bar');
    setTimeframe('all');
    setSoloProject(null);
    setActiveProjects(data ? new Set(data.projects) : null);
    setSelectedPieProject(null);
  };

  const chartTitle =
    view === 'weekly'
      ? 'Weekly Production Escapes — Stacked by Project'
      : 'Monthly Production Escapes — Stacked by Project';

  const chartSubtitle =
    timeframe === 'all' ? 'All time · 180d dataset' : `Last ${timeframe} days`;

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col bg-slate-50 p-6">
        <div className="animate-pulse">
          <div className="mb-4 grid grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-28 rounded-xl bg-white shadow-sm" />
            ))}
          </div>
          <div className="mb-4 rounded-xl bg-white p-6 shadow-sm">
            <div className="mb-3 h-4 w-1/3 rounded bg-slate-200" />
            <div className="h-80 rounded-lg bg-slate-100" />
          </div>
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <div className="mb-3 h-4 w-1/4 rounded bg-slate-200" />
            <div className="h-64 rounded-lg bg-slate-100" />
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex flex-col bg-slate-50 p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-base font-semibold text-red-700">
            Failed to load escape tracker data
          </p>
          <p className="mt-1 text-sm text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const isAllActive = effectiveActive.size === data.projects.length;
  const heatmapHeight = Math.max(220, heatmapData.series.length * 36 + 80);

  return (
    <div className="flex flex-col bg-slate-50">
      {/* ── Page title bar ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-6 py-3">
        <span className="text-lg">📊</span>
        <div className="flex flex-1 items-baseline gap-2">
          <span className="text-sm font-semibold text-slate-800">
            Production Escape Tracker
          </span>
          <span className="text-xs text-slate-400">
            Trajector Services · Quality &amp; Release
          </span>
        </div>
        <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs text-slate-500">
          Updated{' '}
          {data.summary.run_timestamp
            ? new Date(data.summary.run_timestamp).toLocaleString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short',
              })
            : data.summary.run_date
              ? new Date(
                  data.summary.run_date + 'T00:00:00'
                ).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '—'}
        </span>
        <span className="rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600">
          Discovery Environment = Production
        </span>
      </div>

      {/* ── Controls ───────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-6 overflow-x-auto border-b border-slate-200 bg-white px-6 py-2.5">
        <div className="flex flex-shrink-0 items-center gap-2.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            View
          </span>
          <div className="flex gap-0.5 rounded-lg border border-slate-200 bg-slate-100 p-0.5">
            {(['weekly', 'monthly'] as ViewMode[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={clsx(
                  'rounded-md px-3.5 py-1.5 text-xs font-medium capitalize transition-all',
                  view === v
                    ? 'bg-white font-semibold text-blue-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                )}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        <div className="h-5 w-px flex-shrink-0 bg-slate-200" />

        <div className="flex flex-shrink-0 items-center gap-2.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Chart
          </span>
          <div className="flex gap-0.5 rounded-lg border border-slate-200 bg-slate-100 p-0.5">
            {(['bar', 'line'] as ChartType[]).map((c) => (
              <button
                key={c}
                onClick={() => setChartType(c)}
                className={clsx(
                  'rounded-md px-3.5 py-1.5 text-xs font-medium capitalize transition-all',
                  chartType === c
                    ? 'bg-white font-semibold text-blue-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="h-5 w-px flex-shrink-0 bg-slate-200" />

        <div className="flex flex-shrink-0 items-center gap-2.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Timeframe
          </span>
          <select
            value={timeframe}
            onChange={(e) => {
              setTimeframe(e.target.value as Timeframe);
              setSelectedPieProject(null);
            }}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:border-blue-400 focus:outline-none"
          >
            <option value="all">All Time</option>
            <option value="30">Last 30 Days</option>
            <option value="60">Last 60 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="180">Last 180 Days</option>
          </select>
        </div>

        <div className="flex-1" />

        <button
          onClick={handleReset}
          className="flex-shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 transition-all hover:border-red-300 hover:text-red-500"
        >
          ↺ Reset
        </button>
      </div>

      {/* ── Main ───────────────────────────────────────────────────────────── */}
      <main className="mx-auto w-full max-w-7xl flex-1 p-6">
        {/* ── KPI Row ──────────────────────────────────────────────────────── */}
        <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            {
              label: 'Filtered Escapes',
              value: kpiFilteredCount,
              sub:
                timeframe === 'all'
                  ? `${data.summary.date_range.start} → ${data.summary.date_range.end}`
                  : `Last ${timeframe} days`,
              showDelta: true,
              accent: 'bg-blue-500',
              tint: 'bg-blue-50',
              valueColor: 'text-blue-700',
            },
            {
              label: 'Projects Affected',
              value: kpiProjectsAffected,
              sub: `of ${data.summary.projects_affected} total`,
              showDelta: false,
              accent: 'bg-rose-500',
              tint: 'bg-rose-50',
              valueColor: 'text-rose-700',
            },
            {
              label: 'Peak Period',
              value: kpiPeak.label,
              sub: `${kpiPeak.count} escape${kpiPeak.count !== 1 ? 's' : ''}`,
              showDelta: false,
              accent: 'bg-amber-500',
              tint: 'bg-amber-50',
              valueColor: 'text-amber-700',
            },
            {
              label: 'Avg / Week',
              value: kpiAvgPerWeek,
              sub: 'escapes per week',
              showDelta: view === 'weekly',
              accent: 'bg-emerald-500',
              tint: 'bg-emerald-50',
              valueColor: 'text-emerald-700',
            },
          ].map(
            ({ label, value, sub, showDelta, accent, tint, valueColor }) => (
              <div
                key={label}
                className={clsx(
                  'relative overflow-hidden rounded-xl border border-slate-200 p-5 shadow-sm',
                  tint
                )}
              >
                <div
                  className={clsx('absolute left-0 top-0 h-1 w-full', accent)}
                />
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  {label}
                </p>
                <p
                  className={clsx(
                    'mt-2 truncate text-3xl font-bold tabular-nums',
                    valueColor
                  )}
                >
                  {value}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {sub}
                  {showDelta && <DeltaBadge delta={trendDelta.delta} />}
                </p>
              </div>
            )
          )}
        </div>

        {/* ── Bar / Line Chart ─────────────────────────────────────────────── */}
        <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {chartTitle}
              </p>
              <p className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
                <span>{chartSubtitle}</span>
                {chartType === 'bar' && bucketKeys.length >= 2 && (
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-0.5 w-5 rounded bg-slate-400" />
                    <span>4-wk rolling avg</span>
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="px-4 pb-2">
            {bucketKeys.length === 0 ? (
              <div className="flex h-72 flex-col items-center justify-center gap-2 rounded-lg bg-slate-50">
                <span className="text-2xl">📭</span>
                <p className="text-sm font-medium text-slate-500">
                  No escapes match the current filters
                </p>
                <p className="text-xs text-slate-400">
                  Try widening the timeframe or selecting more projects
                </p>
              </div>
            ) : (
              <ReactApexChart
                key={`bar-${view}-${chartType}-${timeframe}-${effectiveActive.size}`}
                options={chartOptions}
                series={chartSeries}
                type={chartType === 'bar' ? 'bar' : 'line'}
                height={360}
              />
            )}
          </div>

          {/* Legend */}
          <div className="border-t border-slate-100 bg-slate-50 px-6 py-4">
            <p className="mb-2.5 text-xs text-slate-400">
              Click a bar segment to open that project's issues in Jira · Click
              a project pill to toggle · ⌘/Ctrl+click to solo
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleLegendAll}
                className={clsx(
                  'inline-flex items-center rounded-full border-2 px-3 py-1 text-xs font-bold tracking-wider transition-all',
                  isAllActive
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-slate-200 bg-white text-slate-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500'
                )}
              >
                ALL
              </button>
              {pieData.length > 5 && (
                <button
                  onClick={handleTopFive}
                  className={clsx(
                    'inline-flex items-center rounded-full border-2 px-3 py-1 text-xs font-bold tracking-wider transition-all',
                    isTopFiveActive
                      ? 'border-violet-500 bg-violet-50 text-violet-600'
                      : 'border-slate-200 bg-white text-slate-400 hover:border-violet-400 hover:bg-violet-50 hover:text-violet-500'
                  )}
                >
                  TOP 5
                </button>
              )}
              {data.projects.map((proj) => {
                const colorIdx = data.projects.indexOf(proj);
                const color = getColor(colorIdx);
                const isOn = effectiveActive.has(proj);
                const isSolo = soloProject === proj;
                const count = data.proj_totals[proj] ?? 0;
                return (
                  <button
                    key={proj}
                    title={
                      isOn
                        ? `⌘/Ctrl+click to solo ${proj}`
                        : `Click to add ${proj}`
                    }
                    onClick={(e) =>
                      handleLegendClick(proj, e.metaKey || e.ctrlKey)
                    }
                    style={
                      isSolo
                        ? { boxShadow: `0 0 0 3px ${color}` }
                        : isOn
                          ? { borderColor: color }
                          : undefined
                    }
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-full border-2 px-2.5 py-1 font-mono text-xs font-semibold transition-all',
                      isOn
                        ? 'bg-white text-slate-700 hover:opacity-90'
                        : 'border-slate-200 bg-white text-slate-400 opacity-40 hover:opacity-60'
                    )}
                  >
                    <span
                      className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ background: isOn ? color : '#CBD5E1' }}
                    />
                    <span>{proj}</span>
                    <span className="font-normal text-slate-400">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Project Breakdown Donut ──────────────────────────────────────── */}
        <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">
              Project Breakdown
            </p>
            <p className="mt-0.5 text-xs text-slate-400">
              {pieTotal} escape{pieTotal !== 1 ? 's' : ''} across{' '}
              {pieData.length} project{pieData.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="border-t border-slate-100 p-6">
            {pieData.length > 0 && (
              <p className="mb-3 text-xs text-slate-400">
                Click a segment to select a project
              </p>
            )}
            {selectedPieProject &&
              (() => {
                const entry = pieData.find(([p]) => p === selectedPieProject);
                const count = entry?.[1] ?? 0;
                const pct =
                  pieTotal > 0 ? ((count / pieTotal) * 100).toFixed(1) : '0.0';
                return (
                  <div className="mb-3 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
                    <span
                      className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{
                        background: getColor(
                          data.projects.indexOf(selectedPieProject)
                        ),
                      }}
                    />
                    <span className="font-mono text-sm font-semibold text-slate-700">
                      {selectedPieProject}
                    </span>
                    <span className="text-sm text-slate-500">
                      {count} escapes ({pct}%)
                    </span>
                    <a
                      href={buildJiraUrl(
                        selectedPieProject,
                        data.summary.date_range.start,
                        data.summary.date_range.end
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      View in Jira →
                    </a>
                  </div>
                );
              })()}
            {pieData.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-lg bg-slate-50">
                <span className="text-2xl">📭</span>
                <p className="text-sm font-medium text-slate-500">
                  No data for current filters
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
                <div className="w-64 flex-shrink-0">
                  <ReactApexChart
                    key={`pie-${timeframe}-${effectiveActive.size}`}
                    options={pieOptions}
                    series={pieData.map(([, v]) => v)}
                    type="donut"
                    height={280}
                  />
                </div>
                <div className="flex max-h-72 w-full flex-col gap-0.5 overflow-y-auto">
                  {pieData.map(([proj, count]) => {
                    const colorIdx = data.projects.indexOf(proj);
                    const color = getColor(colorIdx);
                    const pct =
                      pieTotal > 0
                        ? ((count / pieTotal) * 100).toFixed(1)
                        : '0.0';
                    return (
                      <div
                        key={proj}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-slate-50"
                        onClick={() =>
                          window.open(
                            buildJiraUrl(
                              proj,
                              data.summary.date_range.start,
                              data.summary.date_range.end
                            ),
                            '_blank'
                          )
                        }
                      >
                        <span
                          className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                          style={{ background: color }}
                        />
                        <span className="font-mono text-xs font-semibold text-slate-700">
                          {proj}
                        </span>
                        <span className="font-mono text-xs font-semibold text-slate-700">
                          {count}
                        </span>
                        <span className="font-mono text-xs text-slate-400">
                          ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Priority + Status ─────────────────────────────────────────────── */}
        <div className="mb-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {/* Priority breakdown */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="px-6 py-4">
              <p className="text-sm font-semibold text-slate-800">
                Priority Breakdown
              </p>
              <p className="mt-0.5 text-xs text-slate-400">
                Distribution of escape severity in current view
              </p>
            </div>
            <div className="border-t border-slate-100 px-4 pb-4">
              {priorityBreakdown.length === 0 ? (
                <div className="flex h-32 items-center justify-center rounded-lg bg-slate-50">
                  <p className="text-sm text-slate-400">
                    No data for current filters
                  </p>
                </div>
              ) : (
                <ReactApexChart
                  key={`priority-${timeframe}-${effectiveActive.size}`}
                  options={priorityChartOptions}
                  series={[
                    {
                      name: 'Escapes',
                      data: priorityBreakdown.map((p) => p.count),
                    },
                  ]}
                  type="bar"
                  height={Math.max(160, priorityBreakdown.length * 40 + 40)}
                />
              )}
            </div>
          </div>

          {/* Status ratio */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="px-6 py-4">
              <p className="text-sm font-semibold text-slate-800">
                Open / Closed Ratio
              </p>
              <p className="mt-0.5 text-xs text-slate-400">
                Open = Backlog · In Progress · Blocked · Ready for QA · QA In
                Progress · Code Review
              </p>
            </div>
            <div className="border-t border-slate-100 px-4 pb-4">
              {statusRatio.open + statusRatio.closed === 0 ? (
                <div className="flex h-32 items-center justify-center rounded-lg bg-slate-50">
                  <p className="text-sm text-slate-400">
                    No data for current filters
                  </p>
                </div>
              ) : (
                <ReactApexChart
                  key={`status-${timeframe}-${effectiveActive.size}`}
                  options={statusDonutOptions}
                  series={[statusRatio.open, statusRatio.closed]}
                  type="donut"
                  height={240}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── MTTR by Month × Priority ─────────────────────────────────────── */}
        <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">
              MTTR by Month &amp; Priority
            </p>
            <p className="mt-0.5 text-xs text-slate-400">
              Avg days from created → Done · One line per priority · Points with
              n&lt;3 resolved tickets are hidden · Current month may be
              incomplete
            </p>
          </div>
          <div className="border-t border-slate-100 px-4 pb-2 [&_.apexcharts-tooltip]:origin-top-right [&_.apexcharts-tooltip]:scale-90">
            {mttrByMonth.series.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-lg bg-slate-50">
                <p className="text-sm text-slate-400">
                  No resolved data for current filters
                </p>
              </div>
            ) : (
              <ReactApexChart
                key={`mttr-month-${timeframe}-${effectiveActive.size}`}
                options={mttrByMonthOptions}
                series={mttrByMonth.series}
                type="line"
                height={280}
              />
            )}
          </div>
          {/* Summary row — overall MTTR averages per priority */}
          {mttr.length > 0 && mttr.some((r) => r.avgDays !== null) && (
            <div className="border-t border-slate-100 px-6 py-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                All-time averages
              </p>
              <div className="flex flex-wrap gap-5">
                {mttr.map((r) => (
                  <div
                    key={r.priority}
                    className="flex items-center gap-1.5 text-xs text-slate-500"
                  >
                    <span
                      className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                      style={{
                        background: PRIORITY_COLORS[r.priority] ?? '#64748B',
                      }}
                    />
                    <span className="font-medium text-slate-700">
                      {r.priority}
                    </span>
                    <span className="font-bold text-slate-700">
                      {r.avgDays != null ? `${r.avgDays}d` : '—'}
                    </span>
                    <span className="text-slate-400">
                      ({r.resolved} resolved
                      {r.open > 0 && (
                        <span className="text-amber-500"> · {r.open} open</span>
                      )}
                      )
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Heat Map ─────────────────────────────────────────────────────── */}
        <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">
              Escape Heat Map — Projects × Weeks
            </p>
            <p className="mt-0.5 text-xs text-slate-400">
              Most recent 16 weeks · Colour intensity = escape count
            </p>
          </div>
          <div className="border-t border-slate-100 px-4 pb-4">
            {heatmapData.series.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-lg bg-slate-50">
                <span className="text-2xl">📭</span>
                <p className="text-sm font-medium text-slate-500">
                  No data for current filters
                </p>
              </div>
            ) : (
              <ReactApexChart
                key={`heatmap-${timeframe}-${effectiveActive.size}`}
                options={heatmapOptions}
                series={heatmapData.series}
                type="heatmap"
                height={heatmapHeight}
              />
            )}
          </div>
        </div>

        {/* ── Top Offenders Table ─────────────────────────────────────────── */}
        <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">Issues Table</p>
            <p className="mt-0.5 text-xs text-slate-400">
              All-time data · Click a row to view in Jira · Click a column
              header to sort
            </p>
          </div>
          <div className="border-t border-slate-100">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="w-12 px-4 py-3 text-center">#</th>
                  <th className="px-4 py-3">Project</th>
                  <th
                    className="cursor-pointer px-4 py-3 hover:text-blue-600"
                    onClick={() => handleSortToggle('allTime')}
                  >
                    All-Time{' '}
                    {
                      <SortIcon
                        col="allTime"
                        sortCol={sortCol}
                        sortDir={sortDir}
                      />
                    }
                  </th>
                  <th
                    className="cursor-pointer px-4 py-3 hover:text-blue-600"
                    onClick={() => handleSortToggle('last30')}
                  >
                    Last 30d{' '}
                    {
                      <SortIcon
                        col="last30"
                        sortCol={sortCol}
                        sortDir={sortDir}
                      />
                    }
                  </th>
                  <th
                    className="cursor-pointer px-4 py-3 hover:text-blue-600"
                    onClick={() => handleSortToggle('pctOpen')}
                  >
                    % Open{' '}
                    {
                      <SortIcon
                        col="pctOpen"
                        sortCol={sortCol}
                        sortDir={sortDir}
                      />
                    }
                  </th>
                  <th className="px-4 py-3">Top Priority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {topOffendersData.map((row, idx) => {
                  const colorIdx = data.projects.indexOf(row.project);
                  const color = getColor(colorIdx);
                  const pctColor = getPctOpenColor(row.pctOpen);
                  const prioColor =
                    PRIORITY_COLORS[row.topPriority] ?? '#64748B';
                  return (
                    <tr
                      key={row.project}
                      className="cursor-pointer transition-colors hover:bg-slate-50"
                      onClick={() =>
                        window.open(
                          buildJiraUrl(
                            row.project,
                            data.summary.date_range.start,
                            data.summary.date_range.end
                          ),
                          '_blank'
                        )
                      }
                    >
                      <td className="px-4 py-3 text-center text-xs font-bold text-slate-400">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                            style={{ background: color }}
                          />
                          <span className="font-mono text-xs font-semibold text-slate-700">
                            {row.project}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm font-bold text-slate-700">
                        {row.allTime}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-slate-600">
                        {row.last30}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={clsx(
                            'rounded-full px-2 py-0.5 text-xs font-semibold',
                            pctColor
                          )}
                        >
                          {row.pctOpen}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5">
                          <span
                            className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                            style={{ background: prioColor }}
                          />
                          <span className="text-xs text-slate-600">
                            {row.topPriority}
                          </span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Escape Rate Momentum ─────────────────────────────────────────── */}
        <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">
              Escape Rate Momentum — Last 30d vs Prior 30d
            </p>
            <p className="mt-0.5 text-xs text-slate-400">
              ↑ red = more escapes (worse) · ↓ green = fewer escapes (improving)
              · → flat
            </p>
          </div>
          <div className="border-t border-slate-100 p-6">
            {momentum.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-lg bg-slate-50">
                <p className="text-sm text-slate-400">
                  No data for current filters
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {momentum.map((m) => {
                  const colorIdx = data.projects.indexOf(m.project);
                  const projColor = getColor(colorIdx);
                  const arrowColor = getArrowColor(m.arrow);
                  const deltaLabel =
                    m.delta === 0
                      ? ''
                      : m.delta > 0
                        ? `+${m.delta}`
                        : String(m.delta);
                  return (
                    <div
                      key={m.project}
                      className="rounded-xl border border-slate-200 p-3 transition-colors hover:border-slate-300 hover:bg-slate-50"
                    >
                      <div className="mb-1.5 flex items-center justify-between">
                        <span
                          className="font-mono text-xs font-bold"
                          style={{ color: projColor }}
                        >
                          {m.project}
                        </span>
                        <span className={clsx('text-sm font-bold', arrowColor)}>
                          {m.arrow} {deltaLabel}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>
                          <span className="font-semibold text-slate-700">
                            {m.last30}
                          </span>{' '}
                          last 30d
                        </span>
                        <span className="text-slate-300">|</span>
                        <span>
                          <span className="text-slate-500">{m.prior30}</span>{' '}
                          prior
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-white py-4 text-center text-xs text-slate-400">
        Production Escape Tracker · Trajector Services QE ·{' '}
        <strong className="font-medium text-slate-500">
          Discovery Environment = Production
        </strong>{' '}
        · Updated daily at 8:30 AM CT
      </footer>
    </div>
  );
};

export default EscapeTrackerPage;
